import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { categoryService } from '../services/api';
import { Card, Input, Button, PageHeader, Select, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';

const CreateEditCategory = () => {
  const { user } = useAuth();
  // Category Type splits IT's categories into Software / Hardware.
  // BioMedical has no such split, so the field is hidden and never sent.
  const usesCategoryType = (user?.department_code || 'IT') === 'IT';

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryType, setCategoryType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [categoryIdError, setCategoryIdError] = useState('');
  const [categoryTypeError, setCategoryTypeError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Category' : isEditMode ? 'Edit Category' : 'Add Category');
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      // If editing or viewing, fetch the category data
      fetchCategoryData();
    } else if (location.state?.category) {
      // If creating with prefilled data from state
      const category = location.state.category;
      setName(category.name || '');
    }
  }, [id, isEditMode, isViewMode, location.state]);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAll({ page: 1, limit: 1000 });
      const category = response.data.data.find(cat => cat.id === parseInt(id));
      
      if (category) {
        setName(category.name);
        setCategoryId(category.code);
        setCategoryType(category.category_type || '');
      } else {
        toast.error('Category not found');
        navigate('/admin/categories');
      }
    } catch (err) {
      toast.error('Failed to fetch category details');
      console.error(err);
      navigate('/admin/categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setNameError('');
    setCategoryIdError('');
    setCategoryTypeError('');
    setError('');
    
    // Validate
    if (!name.trim()) {
      setNameError('Category name is required');
      return;
    }

    if (usesCategoryType && !categoryType) {
      setCategoryTypeError('Category type is required');
      return;
    }

    try {
      setLoading(true);

      const typeField = usesCategoryType ? { category_type: categoryType } : {};

      if (isEditMode) {
        await categoryService.update(id, { name, ...typeField });
        toast.success('Category updated successfully');
      } else {
        await categoryService.create({ name, categoryId, ...typeField });
        toast.success('Category created successfully');
      }
      
      navigate('/admin/categories');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} category`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/categories');
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading category details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: 'Categories', to: '/admin/categories' },
              { label: isViewMode ? 'View Category' : isEditMode ? 'Edit Category' : 'Add Category' }
            ]}
            className="mb-6"
          />
          {/* Inputs Row: Category Type, Code, then Name */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            {/* Category Type — IT only */}
            {usesCategoryType && (
              <div className="flex-1">
                <Select
                  label="Category Type"
                  name="categoryType"
                  value={categoryType}
                  onChange={(e) => {
                    setCategoryType(e.target.value);
                    if (categoryTypeError) setCategoryTypeError('');
                  }}
                  required
                  error={categoryTypeError}
                  disabled={loading || isViewMode}
                >
                  <option value="">Select Type</option>
                  <option value="Software">Software</option>
                  <option value="Hardware">Hardware</option>
                </Select>
              </div>
            )}
            {/* Category Code/ID */}
            <div className="flex-1">
              <Input
                label={isEditMode || isViewMode ? "Category Code" : "Category ID (Optional)"}
                name="categoryId"
                value={categoryId}
                onChange={isEditMode || isViewMode ? undefined : (e) => {
                  setCategoryId(e.target.value);
                  if (categoryIdError) setCategoryIdError('');
                }}
                placeholder={isEditMode || isViewMode ? undefined : "Auto-generated if left empty"}
                error={categoryIdError}
                maxLength={10}
                disabled={loading || isEditMode || isViewMode}
                readOnly={isEditMode || isViewMode}
              />
            </div>
            {/* Category Name */}
            <div className="flex-1">
              <Input
                label="Category Name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Enter category name"
                required
                error={nameError}
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>
          {/* Actions Row: Buttons at far right */}
          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-primary-600 text-primary-600 hover:bg-primary-50"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Category' : '+ Add Category'}
              </Button>
            )}
            {isViewMode && (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/categories/edit/${id}`)}
              >
                Edit Category
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditCategory;
